/** Normalize API / form errors for roster assignment flows. */

const ERROR_FIELD_MAP = {
  EMPLOYEE_REQUIRED: "employee_id",
  DATE_REQUIRED: "work_date",
  DATE_OUT_OF_PERIOD: "work_date",
  DUPLICATE_ASSIGNMENT: "work_date",
  SHIFT_REQUIRED: "shift_id",
  SHIFT_INVALID: "shift_id",
  OVERRIDE_REASON_REQUIRED: "override_reason",
  ROSTER_PUBLISHED: "override_reason",
};

const ERROR_TITLES = {
  EMPLOYEE_REQUIRED: "Employee required",
  DATE_REQUIRED: "Date required",
  DATE_OUT_OF_PERIOD: "Date outside roster period",
  DUPLICATE_ASSIGNMENT: "Duplicate assignment",
  SHIFT_REQUIRED: "Shift required",
  SHIFT_INVALID: "Invalid shift",
  OVERRIDE_REASON_REQUIRED: "Override reason required",
  ROSTER_PUBLISHED: "Roster is published",
  ROSTER_FROZEN: "Roster is frozen",
  ROSTER_ARCHIVED: "Roster is archived",
  ROSTER_NOT_EDITABLE: "Cannot edit roster",
  VALIDATION_ERROR: "Validation failed",
};

export function isAdminRole(role) {
  const r = String(role || "").toLowerCase();
  return r === "admin" || r === "superadmin" || r === "super_admin";
}

export function rosterApiErrorMessage(error) {
  return rosterApiErrorDetail(error).message;
}

/** Structured error from API or client validation. */
export function rosterApiErrorDetail(error) {
  const d = error?.response?.data?.detail;
  if (typeof d === "object" && d !== null && d.message) {
    const code = d.code || "VALIDATION_ERROR";
    return {
      code,
      message: d.message,
      field: ERROR_FIELD_MAP[code] || null,
      title: ERROR_TITLES[code] || "Assignment failed",
    };
  }
  if (typeof d === "string") {
    return { code: "VALIDATION_ERROR", message: d, field: null, title: "Assignment failed" };
  }
  if (Array.isArray(d)) {
    const message = d.map((x) => x.msg || JSON.stringify(x)).join("; ");
    return { code: "VALIDATION_ERROR", message, field: null, title: "Assignment failed" };
  }
  const message = error?.message || "Request failed";
  return { code: "VALIDATION_ERROR", message, field: null, title: "Assignment failed" };
}

export function validationErrorFromCheck(check) {
  const code = check.code || "VALIDATION_ERROR";
  return {
    code,
    message: check.message || "Validation failed",
    field: ERROR_FIELD_MAP[code] || null,
    title: ERROR_TITLES[code] || "Assignment failed",
  };
}

export function fieldErrorsFromDetail(detail) {
  if (!detail?.field || !detail?.message) return {};
  return { [detail.field]: detail.message };
}

export function normalizeEmployeeId(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value.employee_id) return String(value.employee_id);
  if (typeof value === "object" && value.value) return String(value.value);
  return String(value);
}

export function normalizeShiftId(value) {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value.shift_id) return String(value.shift_id);
  if (typeof value === "object" && value.value) return String(value.value);
  return String(value);
}

/** Build API assignment row (no admin_override in body). */
export function buildAssignmentRow({ employee_id, work_date, shift_id, notes, override_reason }) {
  return {
    employee_id: normalizeEmployeeId(employee_id),
    work_date: work_date || "",
    shift_id: normalizeShiftId(shift_id) || undefined,
    notes: notes || undefined,
    override_reason: override_reason?.trim() || undefined,
  };
}

/**
 * Client-side validation before POST assignments.
 * @returns {{ ok: boolean, message?: string, code?: string }}
 */
export function validateAssignmentSubmit({
  roster,
  canEdit,
  isAdmin,
  requireOverride,
  employee_id,
  work_date,
  shift_id,
  override_reason,
}) {
  const status = roster?.status;

  if (status === "frozen" || status === "archived") {
    return {
      ok: false,
      code: status === "frozen" ? "ROSTER_FROZEN" : "ROSTER_ARCHIVED",
      message:
        status === "frozen"
          ? "Frozen rosters cannot be modified."
          : "Archived rosters cannot be modified.",
    };
  }

  if (!canEdit) {
    if (status === "published" || status === "frozen") {
      return {
        ok: false,
        code: "ROSTER_PUBLISHED",
        message: "Roster is read-only. Use admin override.",
      };
    }
    return {
      ok: false,
      code: "ROSTER_NOT_EDITABLE",
      message: "This roster cannot be edited in its current status.",
    };
  }

  if (requireOverride && !isAdmin) {
    return {
      ok: false,
      code: "ROSTER_PUBLISHED",
      message: "Roster is read-only. Use admin override.",
    };
  }

  const emp = normalizeEmployeeId(employee_id);
  if (!emp) {
    return { ok: false, code: "EMPLOYEE_REQUIRED", message: "Employee selection is required." };
  }

  if (!work_date) {
    return { ok: false, code: "DATE_REQUIRED", message: "Work date is required." };
  }

  if (roster?.period_start && work_date < roster.period_start) {
    return { ok: false, code: "DATE_OUT_OF_PERIOD", message: "Date must be within the roster period." };
  }
  if (roster?.period_end && work_date > roster.period_end) {
    return { ok: false, code: "DATE_OUT_OF_PERIOD", message: "Date must be within the roster period." };
  }

  const shift = normalizeShiftId(shift_id);
  if (!shift) {
    return { ok: false, code: "SHIFT_REQUIRED", message: "Shift selection is required." };
  }

  if (requireOverride && isAdmin && !override_reason?.trim()) {
    return {
      ok: false,
      code: "OVERRIDE_REASON_REQUIRED",
      message: "Override reason is required for published rosters.",
    };
  }

  return { ok: true };
}
